interface ElementMap {
	button: HTMLButtonElement;
	ul: HTMLUListElement;
	li: HTMLLIElement;
}

const CLASS_IS_SUB_MENU_VISIBLE = 'is-sub-menu-visible';

const cloneElement = ( element: Element ) => {
	const doc = new DOMParser().parseFromString(
		element.outerHTML,
		'text/html'
	);

	return doc.body.firstElementChild;
};

const isElement = < T extends keyof ElementMap >(
	e: Element | null,
	name: T
): e is ElementMap[ T ] => e?.nodeName.toLowerCase() === name;

customElements.define(
	'vcp-st-main-navigation',
	class extends HTMLElement {
		protected list: HTMLUListElement | null = null;

		protected primaryMenu: HTMLUListElement | null = null;

		protected mainMenuItems: HTMLLIElement[] | undefined;

		protected dropdownToggle: HTMLElement | null = null;

		protected details: HTMLDetailsElement | null = null;

		protected dropdown: HTMLDListElement | null = null;

		protected dropdownMenu: HTMLUListElement | null = null;

		protected searchInput: HTMLInputElement | null = null;

		protected leaves: HTMLLIElement[] = [];

		protected menuItemsLength = 0;

		protected isComputingSize = false;

		protected isComputingPosition = false;

		protected get dropDownMenuItems() {
			return Array.from(
				this.dropdownMenu?.querySelectorAll( 'li' ) || []
			);
		}

		protected get topOffset() {
			return document.getElementById( 'wpadminbar' )?.offsetHeight ?? 0;
		}

		public connectedCallback() {
			this.list = this.querySelector( 'ul' );
			if ( ! this.list ) {
				throw new Error( 'Missing UL in dropdown navigation.' );
			}

			this.primaryMenu =
				this.querySelector< HTMLUListElement >( '#primary-menu' );
			this.mainMenuItems = Array.from( this.list.children ).filter(
				( e ) => isElement( e, 'li' )
			);
			this.dropdown = this.querySelector( '#main-nav-dropdown' );
			this.dropdownToggle = this.querySelector( 'summary' );
			this.dropdownMenu = this.querySelector( '#main-nav-dropdown-menu' );
			this.details = this.querySelector( 'details' );
			this.searchInput = this.querySelector( '#main-nav-search-input' );
			this.menuItemsLength = this.mainMenuItems.length;

			document.addEventListener( 'click', this.handleClickAnywhere );
			document.addEventListener( 'keydown', this.handleKeydown );
			this.addEventListener( 'click', this.toggleSubMenu );
			addEventListener( 'resize', this.handleWindowResize );
			addEventListener( 'scroll', this.handleWindowScroll );
			this.details?.addEventListener(
				'toggle',
				this.handleDropdownMenuToggle
			);
			this.classList.remove( 'is-initializing' );
			this.computeInitialItemNumber();
			this.handleWindowScroll();
		}

		public disconnectedCallback() {
			document.removeEventListener( 'click', this.handleClickAnywhere );
			document.removeEventListener( 'keydown', this.handleKeydown );
			this.removeEventListener( 'click', this.toggleSubMenu );
			removeEventListener( 'resize', this.handleWindowResize );
			removeEventListener( 'scroll', this.handleWindowScroll );
			this.details?.removeEventListener(
				'toggle',
				this.handleDropdownMenuToggle
			);
		}

		protected closeAllSubMenus() {
			if ( this.leaves.length ) {
				for ( const leave of this.leaves ) {
					leave.classList.remove( CLASS_IS_SUB_MENU_VISIBLE );
				}
				this.leaves.length = 0;
			}
		}

		protected closeDropdown() {
			if ( this.details?.hasAttribute( 'open' ) ) {
				this.details?.removeAttribute( 'open' );
			}
		}

		protected handleWindowResize = () => {
			this.closeAllSubMenus();
			this.closeDropdown();

			if ( this.isComputingSize ) {
				return;
			}
			this.isComputingSize = true;

			requestAnimationFrame( () => {
				if ( ! this.dropdownToggle || ! this.mainMenuItems ) {
					return;
				}

				this.classList.add( 'is-measuring' );
				//NOTE: bring invisible elements into the element flow to
				//      be able to measure their width...
				this.querySelectorAll(
					'#primary-menu > [data-level="0"].is-hidden'
				).forEach( ( elem ) => {
					elem.classList.add( 'is-invisible' );
					elem.classList.remove( 'is-hidden' );
				} );
				const navigationWidth =
					this.offsetWidth - this.dropdownToggle.offsetWidth;
				const { count: maxNavigationItems } = this.mainMenuItems.reduce(
					( acc, item ) => {
						acc.width += item.offsetWidth;
						acc.count += acc.width <= navigationWidth ? 1 : 0;
						return acc;
					},
					{ count: 0, width: 0 }
				) || { count: -1 };

				if ( maxNavigationItems > -1 ) {
					this.updateHamburgerMenu( maxNavigationItems );
				}

				requestAnimationFrame( () => {
					this.classList.remove( 'is-measuring' );

					// NOTE: align main nav items left when there's only one visible...
					this.primaryMenu?.classList.toggle(
						'main-nav__menu--justify-left',
						this.querySelectorAll(
							'#primary-menu > [data-level="0"]:not(.is-hidden)'
						).length <= 1
					);

					this.isComputingSize = false;
				} );
			} );
		};

		protected handleWindowScroll = () => {
			if ( this.isComputingPosition ) {
				return;
			}
			this.isComputingPosition = true;

			requestAnimationFrame( () => {
				const { top } = this.getBoundingClientRect();
				const boundary = this.topOffset + 5;

				this.classList.toggle( 'is-sticky', top < boundary );

				requestAnimationFrame( () => {
					this.isComputingPosition = false;
				} );
			} );
		};

		protected computeInitialItemNumber() {
			const compute = () =>
				requestAnimationFrame( this.handleWindowResize );

			try {
				document.fonts.ready.then( compute ).catch( compute );
			} catch {
				compute();
			}
		}

		protected updateHamburgerMenu( maxNavigationItems: number ) {
			if (
				! this.dropdownToggle ||
				! this.dropdownMenu ||
				! this.mainMenuItems
			) {
				return;
			}

			if ( maxNavigationItems === this.menuItemsLength ) {
				this.dropdownToggle.classList.add(
					'main-nav-dropdown__toggle--magnifier'
				);
				this.dropdownToggle.classList.remove(
					'main-nav-dropdown__toggle--hamburger'
				);
			} else {
				this.dropdownToggle.classList.remove(
					'main-nav-dropdown__toggle--magnifier'
				);
				this.dropdownToggle.classList.add(
					'main-nav-dropdown__toggle--hamburger'
				);
			}

			if ( maxNavigationItems >= this.mainMenuItems.length ) {
				const items = this.dropDownMenuItems.slice(
					0,
					Math.max(
						maxNavigationItems - this.mainMenuItems.length,
						1
					)
				);

				for ( const item of items ) {
					item.parentElement?.removeChild( item );
				}
			} else if ( maxNavigationItems < this.mainMenuItems.length ) {
				const items = this.mainMenuItems.slice(
					maxNavigationItems - this.mainMenuItems.length
				);

				this.dropdownMenu.innerHTML = '';
				for ( const item of items ) {
					const clone = cloneElement( item );
					if ( ! clone ) {
						continue;
					}

					clone.classList.remove( 'is-invisible' );
					this.dropdownMenu.append( clone );
				}
			}

			this.mainMenuItems.forEach( ( item, i ) => {
				const idx = i + 1;

				item.classList.remove( 'is-invisible' );
				if ( idx > maxNavigationItems ) {
					item.classList.add( 'is-hidden' );
				}

				// Give the right top menu item a special class as long
				// it isn't the first item...
				item.classList.toggle(
					'main-nav__item--last',
					idx === maxNavigationItems && idx > 1
				);
			} );
		}

		protected handleClickAnywhere = ( e: MouseEvent ) => {
			const target = e.target as Element | null;

			if ( this.contains( target ) ) {
				return;
			}

			this.closeDropdown();
			this.closeAllSubMenus();
		};

		protected handleKeydown = ( e: KeyboardEvent ) => {
			if ( e.key === 'Escape' ) {
				this.closeAllSubMenus();
				this.closeDropdown();
			}
		};

		protected toggleSubMenu = ( e: MouseEvent ) => {
			const isTriggeredByKeyboard = e.detail === 0;
			const toggle = e.target as Element | null;
			if ( ! isElement( toggle, 'button' ) ) {
				return;
			}

			const hasSubMenu = isElement( toggle.nextElementSibling, 'ul' );
			if ( ! hasSubMenu ) {
				return;
			}

			const listItem = toggle.closest( 'li' );
			if ( ! isElement( listItem, 'li' ) ) {
				return;
			}

			for ( let i = this.leaves.length - 1; i >= 0; i-- ) {
				if ( ! this.leaves[ i ].contains( listItem ) ) {
					this.leaves[ i ].classList.remove(
						CLASS_IS_SUB_MENU_VISIBLE
					);
					this.leaves.length = i;
					continue;
				}
				break;
			}

			const isOpened = listItem.classList.contains(
				CLASS_IS_SUB_MENU_VISIBLE
			);
			if ( isOpened ) {
				listItem.classList.remove( CLASS_IS_SUB_MENU_VISIBLE );
				toggle.setAttribute( 'aria-expanded', 'false' );
				toggle.nextElementSibling.inert = true;
				return;
			}

			this.leaves.push( listItem );

			// NOTE: close dropdown when sub menu is opened unless the
			//       sub menu is a child of the dropdown...
			if ( ! this.details?.contains( listItem ) ) {
				this.closeDropdown();
			}

			listItem.classList.add( CLASS_IS_SUB_MENU_VISIBLE );
			toggle.setAttribute( 'aria-expanded', 'true' );
			toggle.nextElementSibling.inert = false;

			// NOTE: focus first item in sub menu for keyboard users...
			if ( isTriggeredByKeyboard ) {
				toggle.nextElementSibling.querySelector( 'a' )?.focus();
			}
		};

		protected handleDropdownMenuToggle = () => {
			if ( this.details?.open ) {
				this.closeAllSubMenus();
				this.searchInput?.focus();
			}
		};
	}
);
